/**
 * The client's clock — one number, and everything animated is drawn against it.
 *
 * Every bar in this game is a subtraction: a chop is `tick - resumedAtTick`, a
 * cook is `tick` between two phase stamps, a fuse is `expiresAtTick - tick`,
 * and the round clock is `roundDurationTicks - tick`. So there is exactly one
 * thing to get right, and until now it was got wrong in the same way for all of
 * them: the tick came from the last event that happened to be folded, pushed
 * forward by however long ago that frame arrived. A three-second chop emits
 * nothing but a keyframe every two seconds, so the guess overshot — the bar
 * pinned full, the round clock reached 0:00 while play carried on — and then
 * snapped back the moment a frame landed.
 *
 * This clock is anchored instead. `ROUND_STARTED` carries the wall-clock
 * instant tick 0 was simulated at; the client turns that into an origin on its
 * own timeline and reads the tick off its own clock, correcting toward the
 * server on every frame that carries one. Two properties hold, and both are
 * tested:
 *
 *   - it never returns a smaller number than it returned last, because a bar
 *     that goes backwards reads as the game losing progress the player made;
 *   - it never reads below the last tick the server actually confirmed, because
 *     the server is the authority and drawing behind it is a second kind of lie.
 *
 * Without an anchor — the replay page, the tutorial sandbox, the editor preview
 * — it falls back to extrapolating from the last frame exactly as before, so a
 * consumer that has no round start is unchanged.
 */

import { TICK_MS } from '@overcooked/shared'

/**
 * How hard each frame pulls the offset toward what the server said.
 *
 * Low, deliberately. The correction is applied to a smooth clock a player is
 * watching, so a single late frame must move it by a fraction of a tick rather
 * than jerk the bar; a run of them converges within a second or two, which is
 * far inside the two-second keyframe cadence.
 */
export const SMOOTHING = 0.1

/**
 * How far the unanchored fallback will extrapolate before giving up.
 *
 * Keyframes land every two seconds, so a gap this long means the connection is
 * gone. Guessing further would be a lie drawn on the canvas.
 */
export const MAX_EXTRAPOLATION_TICKS = 300

/**
 * More than one second ahead of a just-arrived server frame is not ordinary
 * network jitter — it means the tab or simulation stalled. Recovering here is
 * preferable to keeping every subsequent progress bar artificially full.
 */
export const MAX_RECOVERABLE_LEAD_TICKS = 50

/**
 * The unanchored estimate: the last tick we were told, plus the time since.
 *
 * This was the whole clock before `TickClock` existed, and it stays as the
 * fallback for feeds that never see a `ROUND_STARTED` — the tutorial sandbox
 * pushes frames only when its engine emits events, so without this a chop bar
 * in the tutorial would freeze between them.
 */
export function estimateTick(lastTick: number, sinceLastFrameMs: number, tickMs: number): number {
  if (sinceLastFrameMs <= 0) return lastTick
  const ahead = Math.min(MAX_EXTRAPOLATION_TICKS, sinceLastFrameMs / tickMs)
  return lastTick + ahead
}

interface TickClockOptions {
  /** Milliseconds per tick. Only overridden by tests. */
  tickMs?: number
  /** The epoch clock the anchor is converted through. Injected for tests. */
  epochNow?: () => number
  smoothing?: number
}

export class TickClock {
  /** Local timestamp of server tick 0, or `null` while unanchored. */
  private origin: number | null = null
  /** The correction from raw elapsed time to the server's tick, in ticks. */
  private offset = 0
  /** The highest tick the server has actually confirmed. */
  private floor = 0
  /** The last number handed out, so the clock cannot run backwards. */
  private last = 0
  /** The unanchored fallback's inputs. */
  private lastTick = 0
  private lastFrameAt = 0

  private readonly tickMs: number
  private readonly epochNow: () => number
  private readonly smoothing: number

  constructor(options: TickClockOptions = {}) {
    this.tickMs = options.tickMs ?? TICK_MS
    this.epochNow = options.epochNow ?? (() => Date.now())
    this.smoothing = options.smoothing ?? SMOOTHING
  }

  get anchored(): boolean {
    return this.origin !== null
  }

  /**
   * Tie server tick 0 to a point on this machine's own timeline.
   *
   * The two clocks are different machines', so the epoch stamp is converted
   * once, here, and never compared again: everything after this is arithmetic
   * on the local monotonic clock. A stamp that is not a finite number — an old
   * log recorded before this field existed — leaves the clock unanchored rather
   * than anchored to `NaN`.
   */
  anchor(startedAtEpochMs: number, nowMs: number): void {
    if (!Number.isFinite(startedAtEpochMs)) return
    this.origin = nowMs - (this.epochNow() - startedAtEpochMs)
    this.offset = 0
    this.floor = 0
    this.last = 0
  }

  /** Back to knowing nothing — a new round, or a replay seeking somewhere else. */
  reset(): void {
    this.origin = null
    this.offset = 0
    this.floor = 0
    this.last = 0
    this.lastTick = 0
    this.lastFrameAt = 0
  }

  /**
   * A frame arrived carrying the server's tick.
   *
   * The offset moves a tenth of the way toward the difference between what the
   * server said and what raw elapsed time says, which is how one-way latency
   * and clock skew are absorbed without anybody measuring either. The floor
   * moves to the tick itself: whatever the arithmetic thinks, the server has
   * demonstrably reached here.
   */
  onFrame(serverTick: number, nowMs: number): void {
    this.lastTick = serverTick
    this.lastFrameAt = nowMs
    if (this.origin === null) return
    const raw = (nowMs - this.origin) / this.tickMs
    // `last` normally prevents a bar from twitching backward on a late frame.
    // But a long suspension can put it seconds ahead of the server; keeping
    // that overshoot forever makes every later chop, wash and cook appear to
    // finish immediately. A fresh authoritative frame is the right moment to
    // discard that impossible lead and start drawing from the real clock.
    if (this.last > serverTick && raw - serverTick > MAX_RECOVERABLE_LEAD_TICKS) {
      this.offset = serverTick - raw
      this.floor = serverTick
      this.last = serverTick
      return
    }
    this.offset += (serverTick - raw - this.offset) * this.smoothing
    this.floor = Math.max(this.floor, serverTick)
  }

  /** Where the server's clock has most likely got to, right now. */
  estimate(nowMs: number): number {
    if (this.origin === null) {
      return estimateTick(this.lastTick, nowMs - this.lastFrameAt, this.tickMs)
    }
    const raw = (nowMs - this.origin) / this.tickMs
    this.last = Math.max(this.last, this.floor, raw + this.offset)
    return this.last
  }
}
